import React, { createContext, useContext, useState, useEffect } from 'react';
import { staffApiService } from '../services/staffApi';
import { useUser } from './UserContext';

const StaffContext = createContext();

export const useStaff = () => {
  const context = useContext(StaffContext);
  if (!context) {
    throw new Error('useStaff must be used within a StaffProvider');
  }
  return context;
};

export const StaffProvider = ({ children }) => {
  const [staffUser, setStaffUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useUser();

  const checkStaffStatus = async () => {
    if (!user?.idToken) {
      setStaffUser(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Try to access the staff dashboard endpoint
      // If successful, user has staff/admin permissions
      await staffApiService.getDashboardOverview(user.idToken, new Date().toISOString().split('T')[0]);
      
      // If successful, user is staff/admin
      setStaffUser({
        lineUserId: user.lineUserId,
        displayName: user.displayName,
        idToken: user.idToken
      });
    } catch (err) {
      // User is not staff/admin - this is normal
      setStaffUser(null);
      setError("User is not authorized to access staff dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.idToken) {
      checkStaffStatus();
    } else {
      setStaffUser(null);
    }
  }, [isAuthenticated, user?.idToken]);

  const value = {
    staffUser,
    loading,
    error,
    isStaff: !!staffUser
  };

  return (
    <StaffContext.Provider value={value}>
      {children}
    </StaffContext.Provider>
  );
};
